import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router-dom';

const OutboundAgentCreate = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSave = async () => {
    if (!name.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter an agent name',
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          agent_type: 'outbound',
          prompt_text: 'You are a helpful assistant.',
          initial_message: 'Hello, how can I help you today?',
          language: 'en-US',
          voice: 'en-US-JennyNeural',
          llm_model: 'gpt-4o'
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create agent');
      }

      setSnackbar({
        open: true,
        message: 'Outbound agent created successfully',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/agents');
      }, 1500);
    } catch (error) {
      console.error('Error creating agent:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create agent',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        elevation={0}
        sx={{ p: 3 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton
            onClick={() => navigate('/agents/new')}
            sx={{ mr: 2 }}
            disabled={saving}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            Create Outbound Agent
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <TextField
            label="Agent Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
            sx={{ mb: 4 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Creating...' : 'Create Agent'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OutboundAgentCreate; 